import { Component, ElementRef, EventEmitter, HostListener, Output, ViewChild } from "@angular/core";
import { GeocodingUrl } from "src/app/constants/url/geocoding.url";
import { LocationUrl } from "src/app/constants/url/location.url";
import { ResponseDto } from "src/app/dto/common/response.dto";
import { GeocodingResponseDto } from "src/app/dto/geocoding/geocodingResponse.dto";
import { PointDto } from "src/app/dto/location/point.dto";
import { AccountService } from "src/app/service/account/account.service";
import { HttpService } from "src/app/service/http/http.service";
import { LocalizationService } from "src/app/service/localization/localization.service";
import { PopupService } from "src/app/service/popup/popup.service";
import { GeocodingUtil } from "src/app/utils/geocoding.util";
import { BookmarkListComponent } from "../bookmark/bookmarkList.component";
import { LocationComponent } from "../location/location.component";
import { TabCode } from "./tab.code";

@Component({
    selector: 'search',
    templateUrl: '../../view/search/search.html'
})
export class SearchComponent {
    public isLayerVisible: boolean = false;
    public isSearchResultListVisible: boolean = false;

    public tabCode: typeof TabCode = TabCode;
    public nowTabCode: TabCode = TabCode.LOCATION;

    public pointDto: PointDto = new PointDto();
    public bookmarkDto!: PointDto;
    public geocodingResponseList: GeocodingResponseDto[] = [];

    @ViewChild(LocationComponent) private locationComponent!: LocationComponent;
    @ViewChild(BookmarkListComponent) private bookmarkListComponent!: BookmarkListComponent;
    @Output() public searchResultEvent: EventEmitter<string> = new EventEmitter<string>();
    @Output() public getWeatherEvent: EventEmitter<{}> = new EventEmitter<{}>();

    private onGeocodingRequest: boolean = false;

    constructor(
        private elementRef: ElementRef, 
        private httpService: HttpService, 
        private accountService: AccountService, 
        private localizationService: LocalizationService,
        private popupService: PopupService) {

        this.setAccountInfo();
        this.setClientCountryInfo();
        this.getLastAltitude();
    }

    private setAccountInfo(): void {
        this.pointDto.accountSeq = this.accountService.getAccountSeq();
    }

    private setClientCountryInfo(): void {
        this.pointDto.isKorean = this.localizationService.isKorean;
        this.pointDto.setTimeOffset(this.localizationService.utcOffset);
    }

    private getLastAltitude(): void {
        this.httpService.get<ResponseDto<number>>(LocationUrl.getLastAltitude(this.accountService.getAccountSeq()))
            .subscribe(response => {
                if (response.isSuccess) {
                    this.pointDto.altitude = response.data;
                    this.getGeolocation(true);
                } else {
                    this.pointDto.altitude = 0;
                }
            });
    }

    @HostListener("document:click", ["$event"])
    public clickOthers(event: MouseEvent): void {
        if ((event.target as HTMLElement).getAttribute("isIgnoreElement")) {
            return;
        }

        this.isLayerVisible = this.elementRef.nativeElement.contains(event.target);
    }

    public closeLayer(): void {
        this.isLayerVisible = false;
    }

    public setTabCode(tabCode: TabCode): void {
        this.nowTabCode = tabCode;

        if (this.nowTabCode == TabCode.LOCATION) {
            this.locationComponent.getList();
        } else if (this.nowTabCode == TabCode.BOOKMARK_LIST) {
            this.bookmarkListComponent.getList();
        }
    }

    public showBookmarkManager(pointDto: PointDto): void {
        this.bookmarkDto = pointDto;

        setTimeout(() => {
            this.nowTabCode = TabCode.BOOKMARK_MANAGER;
        }, 100);
    }

    public closeBookmarkManage(): void {
        setTimeout(() => {
            this.setTabCode(TabCode.BOOKMARK_LIST);
        }, 100);
    }
    
    public getGeolocation(isInit: boolean): void {
        if (GeocodingUtil.isEnable()) {
            GeocodingUtil.getGeolocation(position => {
                this.pointDto.latitude = position.coords.latitude;
                this.pointDto.longitude = position.coords.longitude;
                this.search(true);
            }, () => {
                if (!isInit) {
                    this.popupService.publishAlert(
                        this.localizationService.translate("popup.geolocation.title"),
                        this.localizationService.translate("popup.geolocation.permissionDenied")
                    );
                }
            });
        } else {
            if (!isInit) {
                this.popupService.publishAlert(
                    this.localizationService.translate("popup.geolocation.title"),
                    this.localizationService.translate("popup.geolocation.notSupported")
                );
            }
        }
    }

    public isTypedAddress(): boolean {
        return this.pointDto.address != undefined && this.pointDto.address.length > 0;
    }

    public clear(): void {
        this.pointDto.address = "";
    }

    public search(isReverseGeocoding: boolean): void {
        if (!this.onGeocodingRequest) {
            this.onGeocodingRequest = true;

            this.setClientCountryInfo();
            this.pointDto.isReverse = isReverseGeocoding;
    
            this.httpService.post<ResponseDto<GeocodingResponseDto[]>>(GeocodingUrl.geocoding, this.pointDto.toGeocodingRequestDto())
                .subscribe(response => {
                    this.geocodingResponseList = [];
    
                    if (response.isSuccess) {
                        response.data.forEach(response => {
                            this.geocodingResponseList.push(Object.assign(new GeocodingResponseDto(), response));
                        });
    
                        this.isLayerVisible = true;
                        this.isSearchResultListVisible = true;
                    }

                    this.onGeocodingRequest = false;
                });
        }
    }

    public changeAltitude(): void {
        if (this.pointDto.latitude && this.pointDto.longitude) {
            this.addLocation();
            this.getWeatherData();
    
            this.closeLayer();
        } else {
            this.search(false);
        }
    }

    public setLocation(geocodingResponseDto: GeocodingResponseDto): void {
        this.pointDto.address = geocodingResponseDto.address;
        this.pointDto.latitude = geocodingResponseDto.latitude;
        this.pointDto.longitude = geocodingResponseDto.longitude;
        
        this.addLocation();
        this.getWeatherData();
        
        this.closeSearchResult();
        this.closeLayer();
    }

    private addLocation(): void {
        this.locationComponent.add(this.pointDto.toLocationDto());
    }

    private getWeatherData(): void {
        this.searchResultEvent.emit(this.pointDto.address);
        this.getWeatherEvent.emit(this.pointDto.toSearchDto());
    }

    public onPointClick(pointDto: PointDto): void {
        pointDto.timeOffset = this.pointDto.timeOffset;
        this.pointDto = Object.assign(new PointDto(), pointDto);

        this.getWeatherData();
    }

    public closeSearchResult(): void {
        this.isSearchResultListVisible = false;
    }
}